import { OrderQuotePurpose, useCreateOrderQuoteMutation } from '@codezo/graphql';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { DialogBody, DialogFooter, DialogForm } from '../ui/dialog';
import { Field } from '../ui/field';
import { Input } from '../ui/input';
import { SelectPopover } from '../ui/select-popover';
import { Textarea } from '../ui/textarea';

const PURPOSE_OPTIONS = [
	{ value: OrderQuotePurpose.NotSpecified, label: 'Not specified' },
	{ value: OrderQuotePurpose.Packaging, label: 'Packaging' },
	{ value: OrderQuotePurpose.Event, label: 'Event' },
	{ value: OrderQuotePurpose.Resell, label: 'Resell' },
	{ value: OrderQuotePurpose.Other, label: 'Other' },
];

export interface CreatedQuote {
	id: string;
	name: string;
}

interface CreateQuoteFormProps {
	/** Fired after the quote is created, with the new quote's id + name. */
	onCreated: (quote: CreatedQuote) => void;
	onCancel: () => void;
	submitLabel?: string;
	cancelLabel?: string;
}

/**
 * The "new quote" form, shared between the standalone CreateQuoteModal and the
 * inline create step inside AddToQuoteModal. Renders its own dialog body +
 * footer, so consumers only supply the DialogContent/Header shell.
 */
export function CreateQuoteForm({
	onCreated,
	onCancel,
	submitLabel = 'Create quote',
	cancelLabel = 'Cancel',
}: CreateQuoteFormProps) {
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [purpose, setPurpose] = useState('');

	const [createQuote, { loading }] = useCreateOrderQuoteMutation();

	const handleSubmit = async () => {
		if (!name.trim()) return;

		try {
			const { data } = await createQuote({
				variables: {
					input: {
						name: name.trim(),
						description: description.trim() || undefined,
						purpose: purpose ? (purpose as OrderQuotePurpose) : undefined,
					},
				},
			});

			const created = data?.createOrderQuote;
			if (created?.success && created.referenceKey) {
				toast.success('Quote created');
				onCreated({ id: created.referenceKey, name: name.trim() });
			} else if (created?.success) {
				// Created but no id returned — can't continue into it.
				toast.error('Quote created but could not be opened');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to create quote');
		}
	};

	return (
		<DialogForm onSubmit={handleSubmit}>
			<DialogBody>
				<div className="flex flex-col gap-4">
					<Field label="Name" required>
						<Input
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="e.g. Spring Collection 2026"
							autoFocus
						/>
					</Field>

					<Field label="Description">
						<Textarea
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							placeholder="Brief description of this quote"
							rows={2}
						/>
					</Field>

					<Field label="Purpose">
						<SelectPopover
							value={purpose}
							onChange={setPurpose}
							options={PURPOSE_OPTIONS}
							placeholder="Select purpose"
						/>
					</Field>
				</div>
			</DialogBody>

			<DialogFooter>
				<Button type="button" variant="outline" onClick={onCancel}>
					{cancelLabel}
				</Button>
				<Button type="submit" disabled={!name.trim() || loading}>
					{loading ? 'Creating…' : submitLabel}
				</Button>
			</DialogFooter>
		</DialogForm>
	);
}
